import { Injectable } from '@angular/core';
import {Observable, ReplaySubject, Subscription} from 'rxjs';
import { MenuStructure } from '../models/FSMenu';
import { VenueConfig } from '../models/FSConfig';
import { Order } from '../models/Order';
import {first, map} from 'rxjs/operators';
import { MenuService } from './menu.service';
import { AppState } from '../models/AppState';
import { AuthService } from './auth.service';
import {ConfigService} from './config.service';
import {Identity} from '../models/Identity';
import MoneyUtils, { LangUtils, MenuUtils } from '../utils/Utils';

@Injectable({
  providedIn: 'root'
})
export class AppStateService {

  private firstTime = true;
  venueId?: string;
  private identity?: Identity;
  private appstateSubject = new ReplaySubject<AppState>(1);
  private sub?: Subscription;
  private identSub?: Subscription;
  snapshot?: AppState;

  constructor(private menu: MenuService, private auth: AuthService, private config: ConfigService) { }

  private setupObserver(venueId: string, identity: Identity) {
    this.sub = this.menu.observeMenuConfigOrder(venueId).pipe(
      map<[MenuStructure, VenueConfig, Order], AppState>(res => {
        const [menu, cfg, order] = res;
        const appState = new AppState();
        appState.menu = menu;
        appState.cfg = cfg;
        // Venue currency/locale for every formatted amount and the wallet buttons
        MoneyUtils.configure(cfg?.locale);
        appState.order = order;
        appState.identity = identity; //TODO this seems not be used anywhere?
        appState.venueId = Number(venueId);
        // The guest's own choice, else the venue's language (Norwegian venue -> Norwegian guest app)
        appState.lang = localStorage.getItem("collinsLang") || LangUtils.venueLanguage(cfg);
        return appState;
      })
    ).subscribe(as => {
      this.snapshot = as;
      this.appstateSubject.next(as);
    }, err => {
      console.error("Failed to load app state:", err);
      // Prevent white screen by emitting minimal fallback state
      if (!this.snapshot) {
        const fallbackState = new AppState();
        fallbackState.venueId = this.venueId ? Number(this.venueId) : 0;
        fallbackState.lang = localStorage.getItem("collinsLang") || "sv";
        this.appstateSubject.next(fallbackState);
      }
    });
  }


  private setupConfigObsFirst(venueId: string) {
    this.config.observeConfig(venueId).pipe(first()).subscribe(r => {this.setupObserver(this.venueId!, this.identity!);});
  }

  private initStateService() {
    console.log("Init state service...");
    this.identSub?.unsubscribe();
    this.identSub = this.auth.observeIdentity().subscribe(identity => {
      console.log("Identity changed", identity);
      if (identity != null && identity.venueId != null) {
        if (identity.venueId !== this.venueId) {
          console.log(`Start state service... ${identity.venueId}`);
          this.venueId = identity.venueId;
          this.identity = identity;
          this.setupConfigObsFirst(this.venueId!);
          //this.setupObserver(this.venueId, this.identity);
        }
      } else {
        console.log("Stop state service...");
        this.venueId = undefined;
        this.identity = undefined;
        this.sub?.unsubscribe();
      }
    });
  }

  observeAppState(): Observable<AppState> {
    if (this.identSub == null) {
      this.initStateService();
    }
    return this.appstateSubject;
  }

  navOnMenuPage(pageId: string, appState: AppState, emptyMenu: boolean): any[] | null {
    // If no delivery type is selected, redirect to start page
    if (!appState.order.user_cfg.dt) {
      return [this.venueId, "start"];
    }

    // If the menu is empty, redirect to the correct tab
    const tabRedirect = this.shouldRedirectToTab(pageId, appState, emptyMenu);
    if (tabRedirect) {
      return [this.venueId, "menu", tabRedirect];
    }
    return null;
  }

  shouldRedirectToTab(pageId: string | null, appState: AppState, emptyMenu: boolean = false): string | null {
    // A prebooking shows its guest menus in place of food and drinks
    const prebookMenus = MenuUtils.prebookMenus(appState);
    if (prebookMenus.length) {
      return prebookMenus.some(menu => menu.id === pageId) ? null : prebookMenus[0].id;
    }
    const pagesWithVisibleItems = MenuUtils.getPagesWithVisibleItems(appState);
    if (pagesWithVisibleItems.length === 0) {
      console.log("No pages with visible items, no redirect");
      return null;
    }
    const isFoodEmpty = appState.menu.food?.length === 0;
    const isDrinkEmpty = appState.menu.drinks?.length === 0;

    if (this.firstTime) {
      this.firstTime = false;
      if (appState.cfg.start_tab != null && appState.cfg.start_tab !== pageId) {
        return appState.cfg.start_tab;
      }
    }
    if (pageId === MenuUtils.MENU_PAGE_FOOD && isFoodEmpty && !isDrinkEmpty) {
      console.log("Trying to view food tab, but menu contains no food items, redirect to drinks");
      return MenuUtils.MENU_PAGE_DRINKS;
    }
    if (emptyMenu && pageId === MenuUtils.MENU_PAGE_FOOD && !isDrinkEmpty) {
      console.log("Trying to view food tab, but menu contains no available food items, redirecting to drinks");
      return MenuUtils.MENU_PAGE_DRINKS;
    }
    if (emptyMenu && pageId === MenuUtils.MENU_PAGE_DRINKS && !isFoodEmpty) {
      console.log("Trying to view drinks tab, but menu contains no available drinks items, redirecting to food");
      return MenuUtils.MENU_PAGE_FOOD;
    }

    return null;
  }
}
